import { useEffect, useState } from "react";
import axios from "axios";
import { API_BASE, fmtINR } from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Search, ShieldCheck, Trash2, Lock, CheckCircle2 } from "lucide-react";
import { useParams, useNavigate } from "react-router-dom";
import { toast } from "sonner";

const statusMap = {
  queued: { label: "Queued at printer", color: "bg-blue-500" },
  printing: { label: "Printing now", color: "bg-amber-500 pulse-ring" },
  printed: { label: "Printed! Please collect.", color: "bg-emerald-500" },
  failed: { label: "Failed", color: "bg-red-500" },
  payment_pending: { label: "Awaiting payment", color: "bg-slate-400" },
  sending: { label: "Sending to printer", color: "bg-amber-500" },
  pending_approval: { label: "Awaiting shop approval", color: "bg-amber-500" },
  rejected: { label: "Rejected", color: "bg-red-400" },
  cancelled: { label: "Cancelled", color: "bg-slate-400" },
};

export default function TrackOrder() {
  const { jobNumber } = useParams();
  const nav = useNavigate();
  const [q, setQ] = useState(jobNumber || "");
  const [job, setJob] = useState(null);
  const [err, setErr] = useState("");
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [deleting, setDeleting] = useState(false);

  const load = async (num) => {
    setErr("");
    try { const { data } = await axios.get(`${API_BASE}/public/track/${num}`); setJob(data); } catch { setErr("Job not found"); setJob(null); }
  };

  useEffect(() => { if (jobNumber) load(jobNumber); }, [jobNumber]);
  useEffect(() => {
    if (!job) return;
    // Stop polling once files are deleted or job is in a terminal state — no need to refetch.
    if (job.files_deleted || ["printed", "rejected", "cancelled", "failed"].includes(job.status)) return;
    const t = setInterval(() => load(job.job_number), 4000);
    return () => clearInterval(t);
  }, [job?.job_number, job?.status, job?.files_deleted]);

  const deleteFiles = async () => {
    setDeleting(true);
    try {
      const { data } = await axios.post(`${API_BASE}/public/track/${job.job_number}/delete-files`);
      toast.success(`Removed ${data.deleted_count} file${data.deleted_count === 1 ? "" : "s"} from Print Plus`);
      setConfirmOpen(false);
      load(job.job_number);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not delete files. Please try again in a minute.");
    } finally { setDeleting(false); }
  };

  const canDelete = job && ["printed", "rejected", "cancelled"].includes(job.status) && !job.files_deleted && (job.file_count || 0) > 0;

  return (
    <div className="min-h-screen paper-bg">
      <div className="max-w-md mx-auto p-6 space-y-4">
        <h1 className="font-display text-3xl font-black tracking-tighter">Track your order</h1>
        <div className="flex gap-2">
          <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="e.g. PA-20260215-01234" data-testid="track-input" />
          <Button onClick={() => q && nav(`/track/${q}`)} className="bg-orange-500 hover:bg-orange-600" data-testid="track-btn"><Search className="w-4 h-4" /></Button>
        </div>
        {err && <p className="text-red-500 text-sm">{err}</p>}
        {job && (
          <>
            <Card className="border-slate-200"><CardContent className="p-6 space-y-3">
              <div className="text-xs uppercase tracking-widest text-slate-500">Job</div>
              <div className="font-mono text-lg font-bold" data-testid="track-job-number">{job.job_number}</div>
              <div className="text-sm text-slate-500">at {job.store_name}</div>
              <div className="pt-3">
                <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-slate-50 border border-slate-200 text-sm" data-testid="track-status">
                  <span className={`w-2 h-2 rounded-full ${(statusMap[job.status] || statusMap.queued).color}`} />
                  <span className="font-medium">{(statusMap[job.status] || statusMap.queued).label}</span>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3 pt-3 text-sm">
                <div><div className="text-slate-500 text-xs">Pages</div><div className="font-mono font-semibold">{job.total_pages}</div></div>
                <div><div className="text-slate-500 text-xs">Amount</div><div className="font-mono font-semibold">{fmtINR(job.amount)}</div></div>
              </div>
            </CardContent></Card>

            {/* Delete-my-files card — only when job is finished */}
            {canDelete && (
              <Card className="border-slate-200 bg-white" data-testid="delete-files-card">
                <CardContent className="p-5 space-y-3">
                  <div className="flex items-start gap-3">
                    <div className="w-9 h-9 rounded-lg bg-slate-900 text-white grid place-items-center shrink-0"><ShieldCheck className="w-4 h-4" /></div>
                    <div className="min-w-0">
                      <div className="font-display font-bold text-slate-900">Your privacy, your call</div>
                      <div className="text-xs text-slate-500 mt-1">Your print is done. You can now permanently remove the {job.file_count} uploaded file{job.file_count === 1 ? "" : "s"} from Print Plus.</div>
                    </div>
                  </div>
                  <Button
                    onClick={() => setConfirmOpen(true)}
                    className="w-full h-11 bg-slate-900 hover:bg-slate-800 text-white font-semibold"
                    data-testid="delete-files-btn"
                  >
                    <Trash2 className="w-4 h-4 mr-2" />Delete my files from Print Plus
                  </Button>
                  <div className="text-[10px] text-slate-400 flex items-center gap-1.5"><Lock className="w-3 h-3" />Deletion is permanent · this can't be undone</div>
                </CardContent>
              </Card>
            )}

            {job.files_deleted && (
              <Card className="border-emerald-200 bg-emerald-50/60" data-testid="files-deleted-card">
                <CardContent className="p-5 space-y-1">
                  <div className="flex items-center gap-2">
                    <div className="w-8 h-8 rounded-full bg-emerald-500 text-white grid place-items-center shrink-0"><CheckCircle2 className="w-4 h-4" /></div>
                    <div className="font-display font-bold text-slate-900">Files removed</div>
                  </div>
                  <div className="text-xs text-slate-600 pl-10">
                    Your uploaded documents were permanently deleted from Print Plus{job.files_deleted_at ? ` on ${new Date(job.files_deleted_at).toLocaleString()}` : ""}.
                  </div>
                </CardContent>
              </Card>
            )}
          </>
        )}
      </div>

      {/* Confirm dialog */}
      <Dialog open={confirmOpen} onOpenChange={(o) => !deleting && setConfirmOpen(o)}>
        <DialogContent className="max-w-md" data-testid="delete-confirm-dialog">
          <DialogHeader>
            <DialogTitle className="font-display flex items-center gap-2">
              <Trash2 className="w-5 h-5 text-red-500" />Delete uploaded files?
            </DialogTitle>
            <DialogDescription>
              This permanently removes {job?.file_count || 0} file{(job?.file_count || 0) === 1 ? "" : "s"} you uploaded for <span className="font-mono">{job?.job_number}</span> from Print Plus and its object storage. The shop still keeps a record that the print happened, but not the document itself.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="gap-2 sm:gap-2">
            <Button variant="outline" onClick={() => setConfirmOpen(false)} disabled={deleting} data-testid="delete-cancel">
              Keep files
            </Button>
            <Button onClick={deleteFiles} disabled={deleting} className="bg-red-500 hover:bg-red-600" data-testid="delete-confirm">
              {deleting ? "Deleting…" : "Yes, delete permanently"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
